import { rufe } from './client';
import type { Bezugsart } from './auftraege';
import type { Vertriebsform } from './produkte';

/**
 * Der Zugang zum Angebotsgenerator (Lastenheft Kapitel 6).
 *
 * BETRAEGE SIND ZEICHENKETTEN — dieselbe Regel wie im Produktkatalog: Der
 * Rechenkern fuehrt sie als Festkomma auf `bigint`, und `number` verlaesst
 * ab vierstelligen Betraegen den sicheren Ganzzahlbereich (RND-01). Die
 * Oberflaeche rechnet mit keinem davon; sie zeigt an, was der Server
 * geliefert hat.
 *
 * DIE SUMMEN KOMMEN VOM SERVER UND WERDEN HIER NICHT NACHGERECHNET. Eine
 * Zwischensumme im Browser waere eine zweite Wahrheit neben dem Rechenkern —
 * und sie faellt erst auf, wenn der Anwender einen anderen Betrag sieht, als
 * auf dem PDF steht.
 */

export type Angebotsstatus =
  | 'entwurf'
  | 'versendet'
  | 'verhandlung'
  | 'angenommen'
  | 'abgelehnt'
  | 'abgelaufen';

export const STATUSNAME: Readonly<Record<Angebotsstatus, string>> = {
  entwurf: 'Entwurf',
  versendet: 'versendet',
  verhandlung: 'in Verhandlung',
  angenommen: 'angenommen',
  abgelehnt: 'abgelehnt',
  abgelaufen: 'abgelaufen',
};

/** ANG-02, erste Haelfte: WAS die Zeile ist. */
export type Positionsart = 'artikel' | 'freitext' | 'text' | 'zwischensumme' | 'rabatt';

export const ARTNAME: Readonly<Record<Positionsart, string>> = {
  artikel: 'Artikel',
  freitext: 'Freitext',
  text: 'Textzeile',
  zwischensumme: 'Zwischensumme',
  rabatt: 'Rabattzeile',
};

/** ANG-03, zweite Haelfte: OB die Zeile in die Summe eingeht. */
export type Wertung = 'wirksam' | 'alternativ' | 'optional';

export const WERTUNGSNAME: Readonly<Record<Wertung, string>> = {
  wirksam: 'zählt',
  alternativ: 'Alternative',
  optional: 'optional',
};

export type Preisfuehrung = 'netto' | 'brutto';

export type Belegkondition = {
  readonly stufe: 3 | 4 | 5 | 6;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly art: 'prozent' | 'betrag';
  readonly wert: string;
  readonly nichtKombinierbar?: boolean;
};

export type Herleitungszeile = {
  readonly stufe: number;
  readonly regelart: string;
  readonly regelkennung: string;
  readonly wertDavor: string;
  readonly wertDanach: string;
  readonly ungerundet?: string;
  readonly gekappt?: true;
};

export type Angebotsposition = {
  readonly id: string;
  readonly reihenfolge: number;
  readonly abschnitt: number;
  readonly art: Positionsart;
  readonly wertung: Wertung;
  readonly artikel: string | null;
  readonly artikelnummer: string | null;
  readonly bezeichnung: string;
  readonly beschreibung: string | null;
  readonly menge: string | null;
  readonly mengeneinheit: string | null;
  /** PRD-42: die Preisstellung, unter der die Zeile gerechnet wurde. */
  readonly vertriebsform: Vertriebsform | null;
  /** ANG-25: der Gegenstand der Position — meist ein Exklusivtermin. */
  readonly bezugsart: Bezugsart | null;
  /**
   * ANG-36: „Termin auf Anfrage" oder der bestätigte Termin — nichts dazwischen.
   *
   * Der Wert folgt aus dem Vorgang und wird nicht von Hand gesetzt: Solange
   * eine Alternative unbeantwortet ist, steht die Zeile auf Anfrage.
   */
  readonly optionsstand: 'offen' | 'zugesagt' | 'abgelehnt' | 'vorschlag' | null;
  /**
   * ANG-36: der bestätigte Termin mit Datum UND Uhrzeit.
   *
   * Auftraggeber, 13.09.2026: „im angebot soll im dokument für den kunden auch
   * bei jedem termin das datum und die uhrzeit stehen." Dieselbe Angabe steht
   * in der Maske — was der Kunde liest, soll der Vertrieb vor sich haben.
   */
  readonly terminBeginn: string | null;
  /** ANG-36: „samt dem Datum, bis zu dem er gehalten wird." */
  readonly terminGehaltenBis: string | null;
  /** Die Wünsche, solange keiner bestätigt ist — mit Uhrzeit. */
  readonly wunschtermine: readonly { readonly tag: string; readonly zeit: string | null }[];
  readonly bezugId: string | null;
  readonly bezugVon: string | null;
  readonly bezugBis: string | null;
  readonly einzelpreis: string | null;
  readonly steuerschluessel: string | null;
  readonly steuerProzent: string | null;
  /** ANG-05: ob der Preis aus der Findung stammt oder von Hand gesetzt wurde. */
  readonly preisUebersteuert: boolean;
  /**
   * PRD-12: ob der eingefrorene Einzelpreis die Steuer schon enthält.
   *
   * Der Server liefert es seit Migration 0109; der Beleg rechnet damit —
   * ein brutto gepflegter Preis wird auf einem Nettobeleg zurückgerechnet.
   * NULL bei Bestandszeilen: dort wird nicht umgerechnet.
   */
  readonly istBrutto: boolean | null;
  readonly preisherkunft: string | null;
  readonly rabattProzent: string | null;
  readonly konditionen: readonly Belegkondition[];
  readonly hoechstrabattProzent: string | null;
  readonly herleitung: readonly Herleitungszeile[];
  readonly zeilenbrutto: string | null;
  readonly netto: string | null;
};

export type Steuersatzgruppe = {
  readonly schluessel: string;
  readonly prozent: string;
  readonly netto: string;
  readonly steuer: string;
  readonly brutto: string;
  /** ANG-06: der Hinweistext bei steuerfreien Faellen. */
  readonly hinweis: string | null;
};

export type Summenblock = {
  readonly gruppen: readonly Steuersatzgruppe[];
  readonly belegnetto: string;
  readonly steuerSumme: string;
  readonly belegbrutto: string;
  /** ANG-03: was die Alternativen und Optionen kosten wuerden. */
  readonly nichtGewertet: string;
  readonly nichtDarstellbar: readonly {
    readonly schluessel: string;
    readonly gewuenscht: string;
    readonly ausgewiesen: string;
    readonly abweichung: string;
  }[];
};

export type Angebotszeile = {
  readonly id: string;
  readonly stamm: string;
  readonly version: number;
  readonly angebotsnummer: string;
  readonly kunde: string;
  readonly kundenname: string;
  readonly betreff: string;
  readonly belegdatum: string;
  readonly gueltigBis: string;
  readonly status: Angebotsstatus;
  readonly bearbeiterName: string | null;
  readonly eingefroren: boolean;
  readonly belegbrutto: string;
  readonly positionen: number;
};

export type Angebot = {
  readonly id: string;
  readonly stamm: string;
  readonly version: number;
  readonly angebotsnummer: string;
  readonly kunde: string;
  readonly kundenname: string;
  readonly kundenart: string;
  readonly ansprechpartner: string | null;
  readonly ansprechpartnerName: string | null;
  readonly bearbeiter: string | null;
  readonly bearbeiterName: string | null;
  readonly belegdatum: string;
  readonly gueltigBis: string;
  readonly betreff: string;
  readonly kundenreferenz: string | null;
  readonly kopftext: string | null;
  readonly fusstext: string | null;
  readonly preisfuehrung: Preisfuehrung;
  /** ANG-43: bestimmt die Storno- und Umbuchungsstaffel (BUC-45). */
  readonly vertragsart: Vertragsart;
  /** PAR-03: wer den Beleg vermittelt hat — Autohaus, Fahrschule, Verband. */
  readonly vermittler: string | null;
  readonly vermittlername: string | null;
  readonly vermittlercode: string | null;
  readonly status: Angebotsstatus;
  readonly ablehnungsgrund: string | null;
  readonly ablehnungstext: string | null;
  readonly versendetAm: string | null;
  readonly entschiedenAm: string | null;
  readonly eingefroren: boolean;
  readonly vorgaenger: string | null;
  readonly geloescht: boolean;
  readonly angelegtAm: string;
  readonly stand: string;
  readonly positionen: readonly Angebotsposition[];
  readonly summen: Summenblock;
  readonly versionen: readonly {
    readonly id: string;
    readonly version: number;
    readonly status: Angebotsstatus;
    readonly eingefroren: boolean;
    readonly angelegtAm: string;
  }[];
  /** ANG-16: wohin von hier aus gewechselt werden darf. */
  readonly moeglicheStatus: readonly Angebotsstatus[];
  /** ANG-18: der daraus entstandene Auftrag. Besteht er, ist das Angebot abgeschlossen. */
  readonly auftrag: { readonly id: string; readonly auftragsnummer: string } | null;
};

export type Ablehnungsgrund = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly aktiv: boolean;
};

/* ── Angebote ────────────────────────────────────────────────────────────── */

/**
 * Die Fassung, in der eine Aenderung gelandet ist (ANG-15).
 *
 * „Jede Änderung NACH DEM VERSAND erzeugt eine neue Version." Der Server legt
 * sie von sich aus an; steht `angebot` in der Antwort, muss die Maske dorthin
 * wechseln — sonst zeigt sie eine Fassung, in der ihre Änderung nicht steht.
 */
export type Fassungswechsel = {
  readonly angebot?: { readonly id: string; readonly version: number };
};

export async function liste(filter: {
  readonly kunde?: string;
  readonly status?: Angebotsstatus | '';
  readonly suche?: string;
  readonly alleVersionen?: boolean;
}): Promise<readonly Angebotszeile[]> {
  const teile = new URLSearchParams();
  if (filter.kunde !== undefined && filter.kunde !== '') teile.set('kunde', filter.kunde);
  if (filter.status !== undefined && filter.status !== '') teile.set('status', filter.status);
  if (filter.suche !== undefined && filter.suche !== '') teile.set('suche', filter.suche);
  if (filter.alleVersionen === true) teile.set('alleVersionen', 'true');
  const frage = teile.toString();
  return rufe(`/angebote${frage === '' ? '' : `?${frage}`}`);
}

export async function akte(id: string): Promise<Angebot> {
  return rufe(`/angebote/${id}`);
}

export async function ablehnungsgruende(): Promise<readonly Ablehnungsgrund[]> {
  return rufe('/angebote/ablehnungsgruende');
}

export async function anlegen(eingabe: {
  readonly kunde: string;
  readonly betreff: string;
  readonly belegdatum: string;
  readonly gueltigBis: string;
  readonly ansprechpartner?: string | null;
  readonly kundenreferenz?: string | null;
  readonly kopftext?: string | null;
  readonly fusstext?: string | null;
}): Promise<{ readonly id: string; readonly angebotsnummer: string }> {
  return rufe('/angebote', { verfahren: 'POST', rumpf: eingabe });
}

/**
 * Feldweises Sichern mit `stand` (ERR-13).
 *
 * Der Stand geht IMMER mit. Ohne ihn liesse sich eine gleichzeitige
 * Bearbeitung nicht erkennen, und der zweite Anwender ueberschriebe den
 * ersten, ohne dass es jemand merkt.
 */
export async function feldSichern(
  id: string,
  feld: string,
  wert: string | null,
  stand: string,
): Promise<{ readonly stand: string } & Fassungswechsel> {
  return rufe(`/angebote/${id}`, { verfahren: 'PATCH', rumpf: { stand, [feld]: wert } });
}

export async function positionAnlegen(
  angebot: string,
  eingabe: {
    readonly art: Positionsart;
    readonly wertung?: Wertung;
    readonly abschnitt?: number;
    readonly artikel?: string | null;
    readonly bezeichnung?: string | null;
    readonly beschreibung?: string | null;
    readonly menge?: number | null;
    readonly einzelpreis?: string | null;
    readonly steuerschluessel?: string | null;
    readonly steuerProzent?: string | null;
    readonly rabattProzent?: string | null;
    /** PRD-42: ohne Angabe nimmt der Dienst die erste Form des Artikels. */
    readonly vertriebsform?: Vertriebsform;
    /**
     * ANG-25: der Gegenstand. Steht hier ein Termin, rechnet die Preisfindung
     * mit ihm — Standort (AA-011) und vereinbarte Pauschale (DAT-08).
     */
    readonly bezugsart?: Bezugsart | null;
    readonly bezugId?: string | null;
    readonly bezugVon?: string | null;
    readonly bezugBis?: string | null;
    /**
     * ANG-30: Die Terminoptionsanfrage entsteht MIT der Zeile.
     *
     * Auftraggeber, 13.09.2026: „terminoptionen sollen direkt mit dem anlegen
     * eines artikels im angebot angelegt werden." Die Uhrzeit gehört dazu —
     * Kunden haben oft feste Vorstellungen — und liegt auf der Viertelstunde.
     */
    readonly terminoption?: {
      readonly teilnehmerzahl: number;
      readonly standort: string | null;
      readonly gewuenschteFristBis: string | null;
      readonly wunschtermine: readonly { readonly tag: string; readonly zeit: string | null }[];
    };
  },
): Promise<{ readonly id: string } & Fassungswechsel> {
  return rufe(`/angebote/${angebot}/positionen`, { verfahren: 'POST', rumpf: eingabe });
}

export async function positionLoeschen(
  angebot: string,
  position: string,
): Promise<Fassungswechsel> {
  return rufe(`/angebote/${angebot}/positionen/${position}`, { verfahren: 'DELETE' });
}

/** ANG-04: die ganze Ordnung auf einmal, nicht Zeile fuer Zeile. */
export async function neuOrdnen(
  angebot: string,
  reihenfolge: readonly string[],
): Promise<Fassungswechsel> {
  return rufe(`/angebote/${angebot}/reihenfolge`, { verfahren: 'PUT', rumpf: { reihenfolge } });
}

export async function statusSetzen(
  angebot: string,
  status: Angebotsstatus,
  grund: { readonly ablehnungsgrund?: string | null; readonly ablehnungstext?: string | null } = {},
  /** ANG-39: bei der Annahme je Anfrage der Termin, den der Kunde genommen hat. */
  terminwahl: readonly { readonly anfrage: string; readonly alternative: string }[] = [],
): Promise<void> {
  await rufe(`/angebote/${angebot}/status`, {
    verfahren: 'PUT',
    rumpf: { status, ...grund, ...(terminwahl.length === 0 ? {} : { terminwahl }) },
  });
}

/** ANG-15: eine neue Version. Die bisherige wird dabei eingefroren. */
export async function neueVersion(
  angebot: string,
): Promise<{ readonly id: string; readonly version: number }> {
  return rufe(`/angebote/${angebot}/versionen`, { verfahren: 'POST' });
}

export async function loeschen(angebot: string): Promise<void> {
  await rufe(`/angebote/${angebot}`, { verfahren: 'DELETE' });
}

/**
 * ANG-43: die Vertragsart. Sie bestimmt die Storno- und Umbuchungsstaffel
 * (BUC-45) und haengt an der Kundenart: Verbraucher fahren nach AGB § 6,
 * Unternehmer und Behoerden nach § 7.
 */
export type Vertragsart =
  | 'standardtarif'
  | 'flextarif'
  | 'sicherheitstraining'
  | 'veranstaltung';

export const VERTRAGSARTNAME: Record<Vertragsart, string> = {
  standardtarif: 'Standardtarif',
  flextarif: 'Flextarif',
  sicherheitstraining: 'Sicherheitstraining',
  veranstaltung: 'Veranstaltung oder Vermietung',
};

/** Welche Arten zu einer Kundenart gehoeren (ANG-43, BUC-45). */
export function vertragsartenZurKundenart(kundenart: string): readonly Vertragsart[] {
  return kundenart === 'privat'
    ? ['standardtarif', 'flextarif']
    : ['sicherheitstraining', 'veranstaltung'];
}
